import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { END_POINT } from "../../constants/defaultValues";
import "../../containers/navs/sidebar-admin.scss";

const CustomerDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    totalAffiliates: 0,
    pendingApprovals: 0,
    pendingPayouts: 0,
    packageBreakdown: [],
    activePackageBreakdown: [],
    changes: { users: 0, affiliates: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [storeList, setStoreList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStoreStats, setSelectedStoreStats] = useState(null);
  const [loadingStoreStats, setLoadingStoreStats] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setError(null);
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch stats from affiliate admin API (which now includes User/License data)
      const res = await axios.get(`${END_POINT}/api/affiliate/admin/stats`, {
        headers,
      });

      if (res.data) {
        setStats({
          totalUsers: res.data.total_users || 0,
          activeUsers: res.data.active_users || 0,
          inactiveUsers: res.data.inactive_users || 0,
          totalAffiliates: res.data.total_affiliates || 0,
          pendingApprovals: res.data.pending_approvals || 0,
          pendingPayouts: res.data.pending_payouts || 0,
          packageBreakdown: res.data.package_breakdown || [],
          activePackageBreakdown: res.data.active_package_breakdown || [],
          changes: res.data.changes || { users: 0, affiliates: 0 },
        });

        // Set store list
        if (res.data.store_list) {
          setStoreList(res.data.store_list);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      const msg = error.response?.data?.responseText || error.response?.data?.error || error.message || "Lỗi không xác định";
      setError(msg);
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  const formatChange = (value) => {
    if (value > 0) return `+${value}%`;
    if (value < 0) return `${value}%`;
    return "0%";
  };

  // Build package breakdown string in fixed order: Enterprise → Pro Auto → Pro → Basic → Free
  // Map database values (lowercase) to display names
  const packageDisplayOrder = [
    { dbNames: ["enterprise"], display: "Enterprise" },
    { dbNames: ["pro_auto", "pro auto"], display: "Pro Auto" },
    { dbNames: ["pro", "premium"], display: "Pro" },
    { dbNames: ["basic"], display: "Basic" },
    { dbNames: ["free", null, ""], display: "Free" },
  ];

  const packageMap = {};
  stats.packageBreakdown.forEach((pkg) => {
    const name = (pkg.name || "free").toLowerCase().trim();
    packageMap[name] = (packageMap[name] || 0) + pkg.count;
  });

  const packageBreakdownStr = packageDisplayOrder
    .map(({ dbNames, display }) => {
      const count = dbNames.reduce(
        (sum, name) => sum + (packageMap[name] || 0),
        0
      );
      return `${count} ${display}`;
    })
    .join(" | ");

  const kpiCards = [
    {
      label: "Tổng người dùng",
      value: stats.totalUsers.toLocaleString(),
      change: packageBreakdownStr,
      positive: true,
      color: "teal",
      icon: "simple-icon-people",
    },
    {
      label: "Đang hoạt động (7 ngày)",
      value: stats.activeUsers.toLocaleString(),
      change: (() => {
        // Map database names to display names
        const packageNameMap = {
          enterprise: "Enterprise",
          pro_auto: "Pro Auto",
          "pro auto": "Pro Auto",
          pro: "Pro",
          premium: "Pro",
          basic: "Basic",
          free: "Free",
        };
        return (
          stats.activePackageBreakdown
            .map((pkg) => {
              const displayName =
                packageNameMap[(pkg.name || "free").toLowerCase()] || pkg.name;
              return `${pkg.count} ${displayName}`;
            })
            .join(" | ") || "Không có hoạt động"
        );
      })(),
      positive: stats.activeUsers > 0,
      color: "cyan",
      icon: "simple-icon-graph",
    },
    {
      label: "Tổng Affiliate",
      value: stats.totalAffiliates.toLocaleString(),
      change:
        stats.pendingApprovals > 0
          ? `${stats.pendingApprovals} chờ duyệt`
          : formatChange(stats.changes.affiliates),
      positive: stats.pendingApprovals === 0 && stats.changes.affiliates >= 0,
      color: stats.pendingApprovals > 0 ? "orange" : "indigo",
      icon: "iconsminds-handshake",
    },
    {
      label: "Yêu cầu rút tiền",
      value: stats.pendingPayouts.toLocaleString(),
      change: stats.pendingPayouts > 0 ? "Cần xử lý" : "Đã xử lý hết",
      positive: stats.pendingPayouts === 0,
      color: stats.pendingPayouts > 0 ? "rose" : "emerald",
      icon: "simple-icon-clock",
    },
  ];

  // Determine if running locally
  const isLocal = window.location.hostname.includes("localhost");

  const quickActions = [
    {
      label: "Trang chủ",
      to: "https://vmass.vn",
      icon: "simple-icon-home",
      external: true,
    },
    {
      label: "Trang Affiliate",
      to: isLocal
        ? "http://affiliate.localhost:8080/signin"
        : "https://affiliate.vmass.vn/signin",
      icon: "iconsminds-handshake",
      external: true,
    },
    {
      label: "Landing Affiliate",
      to: isLocal
        ? "http://affiliate.localhost:8080"
        : "https://affiliate.vmass.vn",
      icon: "simple-icon-rocket",
      external: true,
    },
    {
      label: "Trang Automation",
      to: isLocal
        ? "http://automation.localhost:8080"
        : "https://automation.vmass.vn",
      icon: "simple-icon-settings",
      external: true,
      // comingSoon: true,
    },
    {
      label: "Trang API",
      to: isLocal ? "http://api.localhost:8080" : "https://api.vmass.vn",
      icon: "simple-icon-cloud-download",
      external: true,
      // comingSoon: true,
    },
  ];

  const handleViewStoreStats = async (storeId) => {
    setLoadingStoreStats(true);
    try {
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(
        `${END_POINT}/api/affiliate/admin/store-stats/${storeId}`,
        { headers }
      );
      if (res.data) {
        setSelectedStoreStats(res.data);
      }
    } catch (error) {
      console.error("Error fetching store stats:", error);
      alert("Không thể tải thông tin thống kê của cửa hàng");
    } finally {
      setLoadingStoreStats(false);
    }
  };

  const filteredStores = storeList.filter((store) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      (store.name || "").toLowerCase().includes(term) ||
      (store.email || "").toLowerCase().includes(term) ||
      (store.phone || "").toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="admin-dashboard-wrapper">
        <div className="admin-page-header">
          <h1 className="admin-page-title">Dashboard</h1>
          <p className="admin-page-subtitle">Đang tải dữ liệu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard-wrapper">
        <div className="admin-page-header">
          <h1 className="admin-page-title">Dashboard</h1>
          <p className="admin-page-subtitle" style={{ color: "#EF4444" }}>
            Lỗi tải dữ liệu: {error}
          </p>
          <div style={{ marginTop: "16px" }}>
            <button
              onClick={() => {
                setError(null);
                setLoading(true);
                fetchDashboardData();
              }}
              className="admin-quick-btn"
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              Thử lại
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loadingStoreStats) {
    return (
      <div className="admin-dashboard-wrapper">
        <div className="admin-page-header">
          <h1 className="admin-page-title">Dashboard</h1>
          <p className="admin-page-subtitle">Đang tải thông tin chi tiết cửa hàng...</p>
        </div>
      </div>
    );
  }

  if (selectedStoreStats) {
    const tenantKpis = [
      { label: "Sản phẩm", value: selectedStoreStats.counts.products.toLocaleString(), color: "blue", icon: "simple-icon-basket-loaded" },
      { label: "Khách hàng", value: selectedStoreStats.counts.customers.toLocaleString(), color: "green", icon: "simple-icon-people" },
      { label: "Nhân viên", value: selectedStoreStats.counts.staff.toLocaleString(), color: "purple", icon: "iconsminds-business-man" },
      { label: "Bàn ghế / Phòng", value: selectedStoreStats.counts.tables.toLocaleString(), color: "orange", icon: "iconsminds-project" },
      { label: "Đơn hàng", value: selectedStoreStats.counts.orders.toLocaleString(), color: "teal", icon: "simple-icon-handbag" },
      { label: "Hóa đơn", value: selectedStoreStats.counts.bills.toLocaleString(), color: "cyan", icon: "simple-icon-doc" },
      { label: "Nhà cung cấp", value: selectedStoreStats.counts.suppliers.toLocaleString(), color: "indigo", icon: "iconsminds-factory" },
      { label: "Khuyến mãi", value: selectedStoreStats.counts.discounts.toLocaleString(), color: "rose", icon: "simple-icon-present" },
    ];

    const store = selectedStoreStats.store;
    const isExpired = store.expiredDate ? new Date(store.expiredDate) < new Date() : false;

    return (
      <div className="admin-dashboard-wrapper">
        <div className="admin-page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h1 className="admin-page-title">
              {store.companyName || store.fullname || "Cửa hàng"}
            </h1>
            <p className="admin-page-subtitle">
              Tên miền: {store.domain || "N/A"} • Email: {store.email} • SĐT: {store.phone || store.phoneNumber || "N/A"}
            </p>
          </div>
          <button
            onClick={() => setSelectedStoreStats(null)}
            className="admin-quick-btn"
            style={{ padding: "8px 16px", borderRadius: "8px", display: "flex", alignItems: "center", gap: "8px" }}
          >
            <i className="simple-icon-arrow-left"></i>
            <span>Quay lại</span>
          </button>
        </div>

        {/* Store Info Card */}
        <div className="admin-card" style={{ marginBottom: "32px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: "12px", color: "#64748B", marginBottom: "4px", fontWeight: "600", letterSpacing: "0.5px" }}>
              GÓI DỊCH VỤ ĐANG SỬ DỤNG
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: store.package === "free" ? "#9CA3AF" : "#60A5FA",
                  textTransform: "uppercase",
                }}
              >
                Gói {store.package}
              </span>
              <span
                style={{
                  fontSize: "11px",
                  padding: "3px 8px",
                  borderRadius: "4px",
                  fontWeight: "600",
                  background: isExpired ? "rgba(239, 68, 68, 0.15)" : "rgba(16, 185, 129, 0.15)",
                  color: isExpired ? "#EF4444" : "#10B981",
                  border: isExpired ? "1px solid rgba(239, 68, 68, 0.25)" : "1px solid rgba(16, 185, 129, 0.25)",
                }}
              >
                {isExpired ? "Đã hết hạn" : "Đang hoạt động"}
              </span>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "13px", color: "#F3F4F6", fontWeight: "500" }}>
              Hạn sử dụng: {store.expiredDate ? moment(store.expiredDate).format("DD/MM/YYYY") : "Không giới hạn"}
            </div>
            <div style={{ fontSize: "12px", color: "#9CA3AF", marginTop: "4px" }}>
              Ngày đăng ký: {moment(store.createdAt).format("DD/MM/YYYY")}
            </div>
          </div>
        </div>

        {/* KPI Grid */}
        <div className="admin-kpi-grid">
          {tenantKpis.map((kpi, index) => (
            <div key={index} className={`admin-kpi-card ${kpi.color}`} style={{ cursor: "default" }}>
              <div className="admin-kpi-icon">
                <i className={kpi.icon}></i>
              </div>
              <div className="admin-kpi-label">{kpi.label}</div>
              <div className="admin-kpi-value">{kpi.value}</div>
            </div>
          ))}
        </div>

        {/* Integrations */}
        <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#F3F4F6", marginBottom: "16px", marginTop: "8px" }}>
          Liên kết kênh bán hàng
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px", marginBottom: "32px" }}>
          {Object.entries(selectedStoreStats.integrations).map(([key, isConnected]) => {
            const names = {
              shopee: { label: "Shopee", icon: "simple-icon-basket", color: "#EE4D2D" },
              tiktok: { label: "TikTok Shop", icon: "simple-icon-music-tone-alt", color: "#FFFFFF" },
              zalo: { label: "Zalo OA", icon: "simple-icon-bubble", color: "#0068FF" },
              facebook: { label: "Facebook Page", icon: "simple-icon-social-facebook", color: "#1877F2" },
            };
            const channel = names[key] || { label: key, icon: "simple-icon-link", color: "#9CA3AF" };

            return (
              <div
                key={key}
                style={{
                  background: "rgba(30, 41, 59, 0.5)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  borderRadius: "12px",
                  padding: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      background: isConnected ? "rgba(16, 185, 129, 0.15)" : "rgba(107, 114, 128, 0.15)",
                      color: isConnected ? "#10B981" : "#9CA3AF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "18px",
                    }}
                  >
                    <i className={channel.icon} style={isConnected ? { color: channel.color } : {}}></i>
                  </div>
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: "600", color: "#F3F4F6" }}>
                      {channel.label}
                    </div>
                  </div>
                </div>
                <div>
                  <span
                    style={{
                      padding: "3px 8px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      fontWeight: "600",
                      background: isConnected ? "rgba(16, 185, 129, 0.2)" : "rgba(239, 68, 68, 0.2)",
                      color: isConnected ? "#10B981" : "#EF4444",
                      border: isConnected ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid rgba(239, 68, 68, 0.3)",
                    }}
                  >
                    {isConnected ? "Đã kết nối" : "Chưa kết nối"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent Activity Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "16px" }}>
          {/* Recent Orders */}
          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">Đơn hàng gần đây</h3>
            </div>
            {selectedStoreStats.recentOrders.length === 0 ? (
              <p style={{ color: "#9CA3AF", padding: "20px 0", textAlign: "center", fontSize: "13px" }}>
                Không có đơn hàng nào
              </p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", color: "#64748B", textAlign: "left" }}>
                      <th style={{ padding: "8px 0" }}>Mã đơn</th>
                      <th style={{ padding: "8px" }}>Tổng tiền</th>
                      <th style={{ padding: "8px" }}>Trạng thái</th>
                      <th style={{ padding: "8px 0", textAlign: "right" }}>Thời gian</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStoreStats.recentOrders.map((ord) => (
                      <tr key={ord.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                        <td style={{ padding: "10px 0", color: "#F3F4F6", fontWeight: "500" }}>
                          #{ord.code || String(ord.id).substring(0, 8)}
                        </td>
                        <td style={{ padding: "10px", color: "#10B981", fontWeight: "600" }}>
                          {formatCurrency(ord.total_price || ord.totalPrice)}
                        </td>
                        <td style={{ padding: "10px" }}>
                          <span
                            style={{
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontSize: "10px",
                              background: ord.status_id === 555 || ord.statusId === 555 ? "rgba(16, 185, 129, 0.2)" : "rgba(59, 130, 246, 0.2)",
                              color: ord.status_id === 555 || ord.statusId === 555 ? "#10B981" : "#60A5FA",
                            }}
                          >
                            {ord.status_id === 555 || ord.statusId === 555 ? "Thành công" : "Mới tạo"}
                          </span>
                        </td>
                        <td style={{ padding: "10px 0", textAlign: "right", color: "#9CA3AF" }}>
                          {moment(ord.createdAt).format("DD/MM HH:mm")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent Bills */}
          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">Hóa đơn gần đây</h3>
            </div>
            {selectedStoreStats.recentBills.length === 0 ? (
              <p style={{ color: "#9CA3AF", padding: "20px 0", textAlign: "center", fontSize: "13px" }}>
                Không có hóa đơn nào
              </p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", color: "#64748B", textAlign: "left" }}>
                      <th style={{ padding: "8px 0" }}>Mã HĐ</th>
                      <th style={{ padding: "8px" }}>Tổng tiền</th>
                      <th style={{ padding: "8px" }}>Thanh toán</th>
                      <th style={{ padding: "8px 0", textAlign: "right" }}>Thời gian</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStoreStats.recentBills.map((bill) => (
                      <tr key={bill.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                        <td style={{ padding: "10px 0", color: "#F3F4F6", fontWeight: "500" }}>
                          #{bill.code || String(bill.id).substring(0, 8)}
                        </td>
                        <td style={{ padding: "10px", color: "#10B981", fontWeight: "600" }}>
                          {formatCurrency(bill.total_price || bill.totalPrice)}
                        </td>
                        <td style={{ padding: "10px" }}>
                          <span
                            style={{
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontSize: "10px",
                              background: bill.payment_status === 555 || bill.paymentStatus === 555 ? "rgba(16, 185, 129, 0.2)" : "rgba(245, 158, 11, 0.2)",
                              color: bill.payment_status === 555 || bill.paymentStatus === 555 ? "#10B981" : "#F59E0B",
                            }}
                          >
                            {bill.payment_status === 555 || bill.paymentStatus === 555 ? "Đã trả" : "Chưa trả"}
                          </span>
                        </td>
                        <td style={{ padding: "10px 0", textAlign: "right", color: "#9CA3AF" }}>
                          {moment(bill.createdAt).format("DD/MM HH:mm")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-dashboard-wrapper">
        <div className="admin-page-header">
          <h1 className="admin-page-title">Dashboard</h1>
          <p className="admin-page-subtitle">Đang tải dữ liệu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-wrapper">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Dashboard</h1>
        <p className="admin-page-subtitle">
          Tổng quan hệ thống VMASS - Phần mềm quản lý bán hàng
        </p>
      </div>

      {/* KPI Cards */}
      <div className="admin-kpi-grid">
        {kpiCards.map((kpi, index) => (
          <div key={index} className={`admin-kpi-card ${kpi.color}`}>
            <div className="admin-kpi-icon">
              <i className={kpi.icon}></i>
            </div>
            <div className="admin-kpi-label">{kpi.label}</div>
            <div className="admin-kpi-value">{kpi.value}</div>
            <div
              className={`admin-kpi-change ${
                kpi.positive ? "positive" : "negative"
              }`}
            >
              {kpi.change}
            </div>
          </div>
        ))}
      </div>

      {/* Content Grid */}
      <div className="admin-content-grid">
        {/* Store List */}
        <div
          className="admin-card"
          style={{ maxHeight: "500px", overflow: "auto" }}
        >
          <div className="admin-card-header" style={{ marginBottom: "12px" }}>
            <h3 className="admin-card-title">
              Danh sách cửa hàng ({filteredStores.length} / {storeList.length})
            </h3>
          </div>
          
          <div style={{ padding: "0 16px 12px 16px" }}>
            <input
              type="text"
              placeholder="Tìm kiếm cửa hàng (Tên, Email, SĐT)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                background: "rgba(15, 23, 42, 0.4)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "8px",
                color: "#F3F4F6",
                fontSize: "13px",
                outline: "none",
              }}
            />
          </div>

          <div style={{ padding: "0 16px" }}>
            {filteredStores.length === 0 ? (
              <p
                style={{
                  color: "#9CA3AF",
                  textAlign: "center",
                  padding: "20px",
                }}
              >
                Không tìm thấy cửa hàng nào
              </p>
            ) : (
              filteredStores.map((store, index) => {
                // Package name mapping
                const packageNameMap = {
                  enterprise: "Enterprise",
                  pro_auto: "Pro Auto",
                  pro: "Pro",
                  premium: "Pro",
                  basic: "Basic",
                  free: "Free",
                };
                const displayPackage =
                  packageNameMap[(store.package || "free").toLowerCase()] ||
                  store.package;

                // Expiry display
                let expiryText = "";
                let expiryColor = "#10B981"; // green
                if (store.isExpired) {
                  expiryText = `Hết hạn ${Math.abs(
                    store.daysUntilExpiry
                  )} ngày`;
                  expiryColor = "#EF4444"; // red
                } else if (store.daysUntilExpiry !== null) {
                  if (store.daysUntilExpiry <= 7) {
                    expiryText = `Còn ${store.daysUntilExpiry} ngày`;
                    expiryColor = "#F59E0B"; // orange
                  } else if (store.daysUntilExpiry <= 30) {
                    expiryText = `Còn ${store.daysUntilExpiry} ngày`;
                    expiryColor = "#FBBF24"; // yellow
                  } else {
                    expiryText = `Còn ${store.daysUntilExpiry} ngày`;
                  }
                }

                return (
                  <div
                    key={store.id}
                    style={{
                      borderBottom: "1px solid rgba(255,255,255,0.1)",
                      padding: "12px 0",
                      marginBottom:
                        index === storeList.length - 1 ? "16px" : "0",
                    }}
                  >
                    {/* Main store */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            color: "#F3F4F6",
                            marginBottom: "4px",
                          }}
                        >
                          {store.name}
                          {store.isActive7Days && (
                            <span
                              style={{
                                marginLeft: "8px",
                                fontSize: "10px",
                                background: "rgba(16, 185, 129, 0.2)",
                                color: "#10B981",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                border: "1px solid rgba(16, 185, 129, 0.3)",
                              }}
                            >
                              Đang hoạt động
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "12px", color: "#9CA3AF" }}>
                          {store.email} • Đăng ký: {store.registeredAtFormatted}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span
                          style={{
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            background:
                              store.package === "free"
                                ? "rgba(107, 114, 128, 0.3)"
                                : "rgba(59, 130, 246, 0.3)",
                            color:
                              store.package === "free" ? "#9CA3AF" : "#60A5FA",
                          }}
                        >
                          {displayPackage}
                        </span>
                        {expiryText && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: expiryColor,
                              marginTop: "4px",
                            }}
                          >
                            {expiryText}
                          </div>
                        )}
                        <div style={{ marginTop: "8px" }}>
                          <button
                            onClick={() => handleViewStoreStats(store.id)}
                            style={{
                              padding: "4px 10px",
                              borderRadius: "6px",
                              fontSize: "11px",
                              background: "rgba(59, 130, 246, 0.15)",
                              color: "#60A5FA",
                              border: "1px solid rgba(59, 130, 246, 0.3)",
                              cursor: "pointer",
                              fontWeight: "500",
                            }}
                          >
                            Chi tiết
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Sub-accounts */}
                    {store.subAccounts && store.subAccounts.length > 0 && (
                      <div
                        style={{
                          marginTop: "8px",
                          paddingLeft: "20px",
                          borderLeft: "2px solid rgba(255,255,255,0.1)",
                        }}
                      >
                        {store.subAccounts.map((sub) => (
                          <div
                            key={sub.id}
                            style={{
                              fontSize: "12px",
                              color: "#9CA3AF",
                              padding: "4px 0",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>
                              └ {sub.fullname} ({sub.email})
                            </span>
                            <span style={{ color: "#6B7280" }}>
                              Tài khoản phụ
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Thao tác nhanh</h3>
          </div>
          <div className="admin-quick-actions">
            {quickActions.map((action, index) =>
              action.external ? (
                <a
                  key={index}
                  href={action.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`admin-quick-btn ${
                    action.comingSoon ? "coming-soon" : ""
                  }`}
                  style={
                    action.comingSoon
                      ? { opacity: 0.6, pointerEvents: "auto" }
                      : {}
                  }
                >
                  <i className={action.icon}></i>
                  <span>{action.label}</span>
                  {action.comingSoon && (
                    <span
                      style={{
                        fontSize: "10px",
                        background: "rgba(255, 193, 7, 0.3)",
                        color: "#FFC107",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        marginLeft: "8px",
                      }}
                    >
                      Sắp ra mắt
                    </span>
                  )}
                </a>
              ) : (
                <Link key={index} to={action.to} className="admin-quick-btn">
                  <i className={action.icon}></i>
                  <span>{action.label}</span>
                </Link>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
